import * as React from 'react';
import {
  MenuItem,
  ListItem,
  InputAdornment,
  Box,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import CustomTextField from '../../../../components/CustomTextField';
import ErrorMessage from '../../../../components/ErrorMessage';
import { XCircle } from '../../../../icons/x-circle';
import { SOCIAL_LINKS_NAMES, SOCIAL_LINKS_VALUES } from '../../../../consts';
import { ArrowDown } from '../../../../icons/arrow-down';
import { ExternalLink } from '../../../../icons/external-link';
import { checkValidUrl } from '../../utils';
import CustomSelect from '../../../../components/CustomSelect';
import { VIEW_MODE } from '../../../../consts/enums';
import { style } from './TalentSocialLink.style';

type TalentSocialLinkItemProps = {
    socialType:string,
    url: string,
    itemId: string,
    removeSocialLinkHandler?: (itemId: string) => void,
    editSocialLinkHandler?: (updatedItem: SocialLinkItem) => void,
    error: SocialLinkError | null,
    disabled?: boolean,
    viewMode: ViewMode
}

export const TalentSocialLinkItem = ({
  socialType,
  url,
  removeSocialLinkHandler,
  itemId,
  editSocialLinkHandler = () => { /* noop */ },
  error,
  disabled = false,
  viewMode,
}:TalentSocialLinkItemProps): JSX.Element => {
  const [socialLink, setSocialLink] = React.useState(socialType || 'OTHER');
  const [selectedUrl, setUrl] = React.useState(url);

  const onValuesChanges = (newSocialType: string, socialUrl: string) => {
    editSocialLinkHandler({
      id: itemId,
      socialType: newSocialType,
      url: socialUrl,
    });
  };

  const handleSelectedLink = (event: SelectChangeEvent<unknown>) => {
    const selectedType = event.target.value as string;
    setSocialLink(selectedType);
    onValuesChanges(selectedType, selectedUrl);
  };

  const handleSelectedUrl = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newUrlValue = event.target.value as string;
    setUrl(newUrlValue);
    onValuesChanges(socialLink, newUrlValue);
  };

  const selectStyles = {
    backgroundColor: error ? 'rgba(209, 67, 67, 0.01)' : 'white',
    maxWidth: '25%',
    flexBasis: '25%',
    marginLeft: '8px',
  };

  const textFieldSx = {
    marginLeft: '60px',
  };

  const inputStyles = {
    backgroundColor: 'transparent',
    padding: 0,
  };

  const textFieldIconStyles = {
    cursor: 'pointer',
  };

  const canRemove = typeof removeSocialLinkHandler === 'function';

  return (
    <>
      <ListItem key={ itemId }
        divider
        style={ {
          paddingTop: '12px',
          paddingBottom: '12px',
          backgroundColor: `${error ? 'rgba(209, 67, 67, 0.12)' : 'transparent'}`,
        } }
      >
        <CustomSelect
          viewMode={ viewMode }
          value={ socialLink }
          variant="standard"
          onChange={ handleSelectedLink }
          displayEmpty
          disableUnderline
          IconComponent={ () => disabled ? null : (
            <ArrowDown
              style={ { transform: 'translate(-80%,30%)' } }
            />
          ) }
          sx={ selectStyles }
          disabled={ disabled }
          style={ { backgroundColor: 'transparent' } }
        >
          <MenuItem value={ SOCIAL_LINKS_VALUES.FACEBOOK }>{SOCIAL_LINKS_NAMES.FACEBOOK}</MenuItem>
          <MenuItem value={ SOCIAL_LINKS_VALUES.GITHUB }>{SOCIAL_LINKS_NAMES.GITHUB}</MenuItem>
          <MenuItem value={ SOCIAL_LINKS_VALUES.LINKEDIN }>{SOCIAL_LINKS_NAMES.LINKEDIN}</MenuItem>
          <MenuItem value={ SOCIAL_LINKS_VALUES.MEDIUM }>{SOCIAL_LINKS_NAMES.MEDIUM}</MenuItem>
          <MenuItem value={ SOCIAL_LINKS_VALUES.TWITTER }>{SOCIAL_LINKS_NAMES.TWITTER}</MenuItem>
          <MenuItem value={ SOCIAL_LINKS_VALUES.OTHER }>{SOCIAL_LINKS_NAMES.OTHER}</MenuItem>
        </CustomSelect>
        <CustomTextField
          viewMode={ viewMode }
          id="basic"
          variant="standard"
          placeholder="Enter link URL"
          InputProps={ {
            onChange: (event) => handleSelectedUrl(event),
            disableUnderline: true,
            style: inputStyles,
          } }
          sx={ textFieldSx }
          value={ selectedUrl }
          fullWidth
          disabled={ disabled }
        />
        <Box sx={ style.Box.sx }>
          { checkValidUrl(selectedUrl) && (
            <Box style={ style.BoxLinkedin  } >
              <a target="_blank" href={ selectedUrl } rel="noreferrer" >
                <ExternalLink />
              </a>
            </Box>
          ) }

          {canRemove && viewMode === VIEW_MODE.EDITABLE && (
            <InputAdornment position="end" style={ textFieldIconStyles }>
              <XCircle
                onClick={ () => {
                  removeSocialLinkHandler(itemId);
                } }
                style={ style.icon }
              />
            </InputAdornment>
          )}
        </Box>
      </ListItem>
      {error && <ErrorMessage message={ error.value } />}
    </>
  );
};

TalentSocialLinkItem.defaultProps = {
  removeSocialLinkHandler: undefined,
  editSocialLinkHandler: () => { /* noop */ },
  disabled: false,
};
